import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import TerminalDetails from '../../modals/details/TerminalDetails';
import { Terminal } from '../../../utils/types/classes/Terminal';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface TerminalGridProps {
    terminals: Terminal[];
}

const TerminalGrid: React.FC<TerminalGridProps> = ({ terminals }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [columnDefs] = useState<ColDef<Terminal>[]>([
        {
            cellRenderer: (params: any) => <TerminalDetails terminal={params.data} />,
            pinned: isMobile ? undefined : 'left',
            width: 100,
        },
        { field: 'id', headerName: 'ID', sortable: true, filter: true, width: 120 },
        { field: 'codeManuel', headerName: 'Code Manuel', sortable: true, filter: true },
        { field: 'androidId', headerName: 'Android ID', sortable: true, filter: true },
        { field: 'localUID', headerName: 'Local UID', sortable: true, filter: true },
        {
            field: 'actif',
            headerName: 'Actif',
            sortable: true,
            filter: true,
            valueFormatter: (params) => (params.value ? 'Oui' : 'Non'),
            width: 100,
            cellDataType: 'boolean',
            cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        },
        {
            field: 'dateMiseEnService',
            headerName: 'Mise en Service',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy') : ''),
            width: 150,
        },
        {
            field: 'dateEnregistrement',
            headerName: 'Enregistrement',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy HH:mm') : ''),
            width: 150,
        },
        { field: 'pwdManager', headerName: 'Mot de passe', sortable: true, filter: true },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const columnKeys = visibleCols.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        gridRef.current.api.exportDataAsCsv({
            fileName: `terminals_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const rowData: Terminal[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }
        const exportData = rowData.map((row) => {
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const field = def.field;
                const header = def.headerName ?? field ?? '';
                if (!field) return;
                let value = (row as any)[field];
                if (field === 'actif') {
                    value = value ? 'Oui' : 'Non';
                } else if (['dateMiseEnService', 'dateEnregistrement'].includes(field)) {
                    value = value ? format(new Date(value), 'dd/MM/yyyy') : '';
                }
                obj[header] = value;
            });
            return obj;
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Terminals');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `terminals_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.id.toString()} rowData={terminals} columnDefs={columnDefs} pagination={true} domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} suppressExcelExport={true} enableCellTextSelection={true} ensureDomOrder={true} animateRows={true} overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default TerminalGrid;
